import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Row = {
  subKey: string;
  bodyKeys: string[];
  img: string;
};

const rows: Row[] = [
  { subKey: 'home.about.sub2', bodyKeys: ['home.about.body2', 'home.about.body3'], img: '/images/generated/curriculum-hero-2026.jpg' },
  { subKey: 'home.about.sub4', bodyKeys: ['home.about.body4'], img: '/images/generated/essential-tea-female-2026.jpg' },
  { subKey: 'home.about.sub5', bodyKeys: ['home.about.body5'], img: '/images/generated/facility-odaiba-img.jpg' },
];

export default function AboutSection() {
  const { t } = useTranslation();
  return (
    <section className="relative py-24 md:py-32 bg-background-50 overflow-hidden">
      <div className="relative max-w-[1280px] mx-auto px-6 sm:px-8 md:px-12 lg:px-16">
        {/* Header + lead statement */}
        <div className="max-w-[900px] mb-16 md:mb-20">
          {String(t('home.about.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.24em] mb-6 font-label">{__s}</p>))}
          <h2 className="font-heading text-[28px] md:text-[38px] leading-[1.4] text-foreground-900 tracking-[0.05em]">
            {t('home.about.title')}
          </h2>
          <div className="gold-line-left mt-6 mb-5"></div>
          {String(t('home.about.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500/70 text-[15px] md:text-base tracking-[0.14em] mb-8">{__s}</p>))}
          <p className="font-heading text-[20px] md:text-[24px] leading-[1.95] tracking-[0.04em] text-foreground-800 border-l-2 border-accent-500/50 pl-6 md:pl-8">
            {t('home.about.body1')}
            <strong className="font-medium text-accent-700">{t('home.about.body1Bold')}</strong>
            {t('home.about.body1End')}
          </p>
        </div>

        {/* Alternating illustrated highlights */}
        <div className="space-y-14 md:space-y-20">
          {rows.map((row, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div key={row.subKey} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className="relative">
                    <div className="absolute -inset-2 border border-accent-500/20 pointer-events-none hidden md:block"></div>
                    <img
                      src={row.img}
                      alt={t(row.subKey)}
                      className="relative w-full aspect-[4/3] object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                </div>
                <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="w-8 h-px bg-accent-500/60"></span>
                    <span className="section-title-en text-accent-500/70 text-[13px] tracking-[0.16em]">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="font-heading text-[21px] md:text-[25px] text-foreground-900 tracking-[0.05em] leading-[1.6] mb-5">
                    {t(row.subKey)}
                  </h3>
                  <div className="space-y-4">
                    {row.bodyKeys.map((key) => (
                      <p key={key} className="text-[16px] md:text-[17px] text-foreground-600 tracking-[0.03em] leading-[2.05]">
                        {t(key)}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <Link
            to="/about"
            className="inline-flex items-center gap-3 text-accent-600 hover:text-accent-500 text-[15px] tracking-[0.12em] transition-colors duration-300 group"
          >
            <span className="w-8 h-px bg-accent-500/60 transition-all duration-300 group-hover:w-12"></span>
            <span>{t('home.about.cta')}</span>
            <i className="ri-arrow-right-line text-sm transition-transform duration-300 group-hover:translate-x-1"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
